import { useEffect, useState } from "react";
import { useNewItem } from "../../toolympus/api/useNewItem";
import { ValidationErrors } from "../../toolympus/components/schemed";
import { Schema } from "../../toolympus/hooks/useSchema";
import { useSchemaUISettings } from '../settings/useSchemaUISettings';

const formData = {
    birthdate: "",
    email: "",
    timezone: "",
    question2: "",
    lastname: "",
    phone: "",
    nationality: "",
    experience: "",
    wa_participation_year: "",
    pdc: "",
    wa_participation: "",
    question1: "",
    ml_document_id: "",
    preferred_name: "",
    title: "",
    middlename: "",
    firstname: "",
    question3: "",
    camera_acknowledged: "",
    cv_document_id: "",
    education: "",
    gender: "",
    country: "",
    avatar_id: null as string | null,
};

export type FormData = typeof formData;

interface QuestionValidationStatus {
  words: number;
  valid: boolean;
  max_words: number;
}



export interface UseApplicationProps {
    schema: Schema;
    isSaved: boolean;
    send: () => Promise<null>;
    clear: () => void;
    isSaving: boolean;
    formState: "success" | "disabled" | "normal";
    errors: ValidationErrors;
    data: FormData;
    update: (changes: Partial<FormData>) => void;
    max_words_count?: Record<string, number>;
    validate: {
        question1: QuestionValidationStatus;
        question2: QuestionValidationStatus;
        question3: QuestionValidationStatus;
    }
}

const regFilter = /[^a-zA-Zа-яА-Я0-9]+/gi;
const calcWords = (text: string) => text.trim().replaceAll(regFilter, " ").split(" ").filter(s => !!s).length;


const constructWordCountValidationStatus = (text: string, maxWords: number | undefined) => {
  const max_words = maxWords || 500;
  const c = calcWords(text);
  return ({
    words: c,
    valid: c <= max_words,
    max_words,
  }
)}

const LocalStorageKey = "__wa_application_form";

const loadFromLS = () => {
    let data = {};
    try {
        const storedStr = localStorage.getItem(LocalStorageKey);
        if(storedStr) {
            data = JSON.parse(storedStr);
        }
    } catch(e) {
    }
    return data;
}

const saveToLS = (data: Partial<FormData>) => {
    const copy = { ...data };
    delete copy.cv_document_id;
    delete copy.ml_document_id;
    localStorage.setItem(LocalStorageKey, JSON.stringify(copy));
}

interface ApplicationSettings {
  max_words_count?: Record<string, number>;
}

export const useApplication = (cfg: ApplicationSettings): UseApplicationProps => {
    const { item, update, save, isLoading: isSaving, errors, startEditing } = useNewItem<FormData, null>(
        "/api/application",
        Object.keys(formData).reduce((obj, key) => ({...obj, [key]: ""}), {}) as FormData
    );

    const { schema, isSchemaNotAvailable } = useSchemaUISettings("/api/application/uiconfig");
    const [isSaved, setIsSaved] = useState(false);

    const formState = isSaved
      ? "success"
      : isSchemaNotAvailable
        ? "disabled"
        : "normal";


    const data = item || formData;
    
    const send = () => save().then(() => { setIsSaved(true); saveToLS({}); return null; });

    useEffect(() => {
        startEditing(loadFromLS());
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if(item) {
            saveToLS(item);
        }
    }, [item]);

    return { 
        schema, 
        isSaved, 
        data, 
        update, 
        clear: () => startEditing({}),
        send, 
        isSaving, 
        formState,
        errors,
        max_words_count: cfg?.max_words_count,
        validate: {
            question1: constructWordCountValidationStatus(data.question1, cfg?.max_words_count?.question1),
            question2: constructWordCountValidationStatus(data.question2, cfg?.max_words_count?.question2),
            question3: constructWordCountValidationStatus(data.question3, cfg?.max_words_count?.question3),
        }
    };
}
